import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DesignCreateStatus } from 'pages/LocationMap/api';

/**
 * Tracks the async POST /designs lifecycle (queued build on a background worker).
 * Transient — blacklisted from persist. The `designCreateListener` middleware owns the
 * poll watch (create-status), refetches the design on READY, and drives this status; the
 * LocationMap component reads `status` for the loader and navigates on READY.
 */
export interface DesignCreateState {
	status: DesignCreateStatus | 'IDLE';
	designUUID: string | null;
	designType: DesignType | null;
	panelKey?: string;
	errorCode: string | null;
}

const initialState: { data: DesignCreateState } = {
	data: {
		status: 'IDLE', designUUID: null, designType: null, panelKey: undefined, errorCode: null,
	},
};

const designCreateSlice = createSlice({
	name: 'designCreate',
	initialState,
	reducers: {
		// POST accepted (202): enter "creating…" and hand the tracking id to the middleware.
		designCreateAccepted(
			state,
			{ payload }: PayloadAction<{ designUUID: string; designType: DesignType; panelKey?: string }>,
		) {
			state.data.status = 'PENDING';
			state.data.designUUID = payload.designUUID;
			state.data.designType = payload.designType;
			state.data.panelKey = payload.panelKey;
			state.data.errorCode = null;
		},

		// Single reconciler for create-status polls.
		syncCreateStatus(
			state,
			{ payload }: PayloadAction<{ designUUID: string; status: DesignCreateStatus; errorCode: string | null }>,
		) {
			state.data.status = payload.status;
			state.data.errorCode = payload.errorCode;
		},

		designCreateFailed(state, { payload }: PayloadAction<{ errorCode: string | null }>) {
			state.data.status = 'FAILED';
			state.data.errorCode = payload.errorCode;
		},

		resetDesignCreate() { return initialState; },
	},
});

export const {
	designCreateAccepted, syncCreateStatus, designCreateFailed, resetDesignCreate,
} = designCreateSlice.actions;

export default designCreateSlice.reducer;
